import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import EmptyState from "@/components/base/EmptyState";
import { getActorById, getFilmography, TYPE_LABELS } from "@/lib/media";
import { usePageMeta } from "@/hooks/usePageMeta";

const PROFILE_ROWS = (actor: NonNullable<ReturnType<typeof getActorById>>) => [
  { label: "出生日期", value: actor.birthday },
  { label: "出生地", value: actor.birthplace },
  { label: "身高", value: actor.height },
  { label: "出道年份", value: actor.debut },
];

export default function ActorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const actor = getActorById(id);
  const works = actor ? getFilmography(actor.id) : [];

  usePageMeta({
    title: actor ? `${actor.name}个人资料与参演作品 - 华丽的外出` : "演员不存在 - 华丽的外出",
    description: actor
      ? `${actor.name}（${actor.enName}）演员资料：${actor.bio[0].slice(0, 62)}… 查看其参演的${works
          .slice(0, 3)
          .map((item) => `《${item.movie.title}》`)
          .join("、")}等作品。`
      : "该演员资料不存在，请返回首页浏览其他内容。",
    keywords: actor ? `${actor.name},${actor.name}个人资料,${actor.name}参演作品,演员名单,华丽的外出` : "华丽的外出,演员",
  });

  if (!actor) {
    return (
      <SiteLayout>
        <div className="shell py-16">
          <EmptyState
            icon="ri-user-search-line"
            title="没有找到这位演员"
            desc="资料可能已被调整，或者链接有误。你可以回到全部作品，从演员名单重新进入。"
            actionTo="/category/all"
            actionLabel="浏览全部作品"
          />
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="shell pt-5 md:pt-7">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-300" />
          <span className="text-foreground-800">演员资料</span>
          <i className="ri-arrow-right-s-line text-sm text-foreground-300" />
          <span className="text-foreground-800">{actor.name}</span>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="ml-auto inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-arrow-left-line" />
            返回上一页
          </button>
        </nav>

        <section className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-secondary-50" aria-label="演员基本信息">
          <div className="grid grid-cols-1 gap-0 md:grid-cols-12">
            <div className="relative md:col-span-4 lg:col-span-3">
              <div className="h-64 w-full bg-secondary-200 md:h-full">
                <img
                  src={actor.avatar}
                  alt={`${actor.name} 头像`}
                  title={`${actor.name} 演员资料`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <span className="absolute left-4 top-4 rounded-md bg-background-50/95 px-2 py-0.5 font-label text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-700">
                Actor Profile
              </span>
            </div>

            <div className="md:col-span-8 lg:col-span-9 md:p-6 lg:p-8">
              <div className="p-5 sm:p-6 md:p-0">
                <div className="flex flex-wrap items-center gap-2">
                  {actor.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md border border-background-300 bg-background-50 px-2 py-0.5 text-[11px] font-medium text-foreground-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-foreground-950 md:text-[38px]">
                  {actor.name}
                </h1>
                <p className="mt-1 font-label text-xs uppercase tracking-[0.2em] text-foreground-400">
                  {actor.enName}
                </p>

                <p className="mt-4 border-l-2 border-accent-400 pl-3 text-sm italic leading-relaxed text-foreground-700">
                  “{actor.quote}”
                </p>

                <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-background-300/70 pt-5 sm:grid-cols-4">
                  {PROFILE_ROWS(actor).map((row) => (
                    <div key={row.label}>
                      <dt className="text-[11px] uppercase tracking-wide text-foreground-400">{row.label}</dt>
                      <dd className="mt-1 text-sm font-medium text-foreground-900">{row.value}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-5 flex flex-wrap items-center gap-2.5">
                  <Link
                    to="/category/all"
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    浏览全部作品
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                  <span className="text-xs text-foreground-500">参演作品 {works.length} 部</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <section aria-label="演员简介">
              <div className="flex items-center gap-2">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                <h2 className="text-lg font-bold text-foreground-950 md:text-xl">个人简介</h2>
              </div>
              <div className="mt-3.5 space-y-3.5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                {actor.bio.map((paragraph, index) => (
                  <p key={index} className="text-sm leading-[1.9] text-foreground-700">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            <section className="mt-8" aria-label="参演作品">
              <div className="mb-4 flex items-end justify-between">
                <div>
                  <h2 className="text-lg font-bold text-foreground-950 md:text-xl">参演作品</h2>
                  <p className="mt-1 text-xs text-foreground-500">按最近更新时间排序</p>
                </div>
                <span className="font-label text-xs font-medium text-foreground-400">{works.length} 部</span>
              </div>

              {works.length === 0 ? (
                <EmptyState
                  icon="ri-film-line"
                  title="暂无收录作品"
                  desc="我们还在整理这位演员的作品列表，欢迎稍后再来看看。"
                  actionTo="/category/all"
                  actionLabel="浏览全部作品"
                />
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {works.map(({ movie, role }) => (
                    <Link
                      key={movie.id}
                      to={`/detail/${movie.id}`}
                      className="group cursor-pointer overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
                    >
                      <div className="aspect-[2/3] w-full overflow-hidden bg-secondary-100">
                        <img
                          src={movie.poster}
                          alt={`《${movie.title}》海报`}
                          title={`${movie.title} 剧情介绍`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="line-clamp-1 text-sm font-semibold text-foreground-950 group-hover:text-primary-700">
                          {movie.title}
                        </h3>
                        <p className="mt-1 line-clamp-1 text-xs text-foreground-500">饰 {role}</p>
                        <p className="mt-1 text-[11px] text-foreground-400">
                          {TYPE_LABELS[movie.type]} · {movie.year}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-4">
                <h2 className="text-base font-bold text-foreground-950">最近参与</h2>
                <div className="mt-3 space-y-2.5">
                  {works.slice(0, 3).map(({ movie, role }) => (
                    <Link
                      key={movie.id}
                      to={`/detail/${movie.id}`}
                      className="group flex cursor-pointer items-center gap-3 rounded-md border border-background-200 bg-background-50 p-2.5 transition-colors hover:border-primary-300"
                    >
                      <div className="h-16 w-11 shrink-0 overflow-hidden rounded bg-secondary-100">
                        <img
                          src={movie.poster}
                          alt={`《${movie.title}》封面`}
                          title={movie.title}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-1 text-sm font-semibold text-foreground-900 group-hover:text-primary-700">
                          {movie.title}
                        </p>
                        <p className="mt-1 line-clamp-1 text-xs text-foreground-500">饰 {role}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-4">
                <p className="text-sm font-semibold text-foreground-950">想补充资料？</p>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                  如果你发现演员信息有误，或者有遗漏的作品，欢迎通过联系我们页面告诉我们。
                </p>
                <Link
                  to="/contact"
                  className="mt-2.5 inline-flex items-center gap-1 text-xs font-medium text-accent-800 hover:underline"
                >
                  前往反馈
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}